import { useCallback, useState } from 'react';

import type {
  RegistryOrder,
  RegistryOrderDetail,
  RegistryOrderQuoteRecordRequest,
} from '@/types/registry-documents';

/**
 * The "Record quote" dialog of a surface that lists orders: which order it
 * is open for, and what its confirm does.
 *
 * One dialog per surface rather than one per row, as for closing: it
 * outlives the row it was opened from, which the re-read after a quote is
 * recorded redraws (the row gains its price and its expected date). The
 * order stays set after the dialog is dismissed: its exit transition
 * still shows it.
 *
 * `record` is the surface's own action (it re-reads the list); a failure
 * is re-thrown for the dialog to show, and leaves it open. `onRecorded`
 * gets the order as it was and as the API returned it, dated.
 */
export function useRegistryOrderQuoteDialog<T extends RegistryOrder>(
  record: (
    orderId: string,
    request: RegistryOrderQuoteRecordRequest
  ) => Promise<RegistryOrderDetail>,
  onRecorded?: (order: T, recorded: RegistryOrderDetail) => void
) {
  const [order, setOrder] = useState<T | null>(null);
  const [opened, setOpened] = useState(false);

  const open = useCallback((target: T) => {
    setOrder(target);
    setOpened(true);
  }, []);

  const dismiss = useCallback(() => setOpened(false), []);

  const confirm = useCallback(
    async (request: RegistryOrderQuoteRecordRequest) => {
      if (!order) {
        return;
      }
      const recorded = await record(order.id, request);
      setOpened(false);
      onRecorded?.(order, recorded);
    },
    [order, record, onRecorded]
  );

  return {
    /** Open the dialog for an order. */
    open,
    /** Spread onto `RegistryOrderQuoteModal`. */
    modalProps: { opened, order, onClose: dismiss, onConfirm: confirm },
  };
}
